import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import type { Promocion } from "../../firebase/firestore/promociones";
import {
  createPromocion,
  updatePromocion,
  generateGradientFromColor,
} from "../../firebase/firestore/promociones";
import { usePromocionStore } from "../../zustand/usePromocionesStore";

interface FormPromocionProps {
  entity?: Promocion;
  onClose: () => void;
}

export default function FormPromocion({ entity, onClose }: FormPromocionProps) {
  const [titulo, setTitulo] = useState(entity?.titulo || "");
  const [descripcion, setDescripcion] = useState(entity?.descripcion || "");
  const [estado, setEstado] = useState<"activo" | "inactivo">(
    entity?.estado || "activo"
  );
  // Color base seleccionado por el usuario (solo uno)
  const [colorCardBase, setColorCardBase] = useState("#0066CC");
  const [colorTexto, setColorTexto] = useState(entity?.colorTexto || "#FFFFFF");
  const [isLoading, setIsLoading] = useState(false);
  const { fetchAllPromociones } = usePromocionStore();

  // Generar degradado a partir del color seleccionado
  const gradient = useMemo(() => {
    return generateGradientFromColor(colorCardBase);
  }, [colorCardBase]);

  useEffect(() => {
    if (entity) {
      setTitulo(entity.titulo);
      setDescripcion(entity.descripcion);
      setEstado(entity.estado);
      // Extraer el color base del objeto color (usar color2 que es el color oscuro)
      if (entity.color && typeof entity.color === 'object') {
        setColorCardBase(entity.color.color2 || entity.color.color1 || "#0066CC");
      } else {
        // Compatibilidad con formato antiguo (string)
        setColorCardBase(typeof entity.color === 'string' ? entity.color : "#0066CC");
      }
      setColorTexto(entity.colorTexto || "#FFFFFF");
    }
  }, [entity]);

  const handleSave = async () => {
    if (!titulo.trim()) {
      toast.error("Debe ingresar un título para la promoción");
      return;
    }

    if (!descripcion.trim()) {
      toast.error("Debe ingresar una descripción para la promoción");
      return;
    }

    setIsLoading(true);

    try {
      // Generar ambos colores del degradado para guardar en Firebase
      const colorGradient = generateGradientFromColor(colorCardBase);
      
      if (entity) {
        await updatePromocion(entity.id, {
          titulo: titulo.trim(),
          descripcion: descripcion.trim(),
          estado,
          color: colorGradient,
          colorTexto,
        });
        toast.success("Promoción actualizada con éxito");
      } else {
        await createPromocion({
          titulo: titulo.trim(),
          descripcion: descripcion.trim(),
          estado,
          color: colorGradient,
          colorTexto,
        });
        toast.success("Promoción creada con éxito");
      }

      await fetchAllPromociones();
      onClose();
    } catch (error) {
      console.error("Error guardando promoción:", error);
      toast.error(
        `Error al ${entity ? "actualizar" : "crear"} la promoción. Intente nuevamente`
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Divider />
      <Box display="flex" flexDirection="column" gap={2} mt={2} px={2}>
        <TextField
          label="Título"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          fullWidth
          required
        />

        <TextField
          label="Descripción"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          fullWidth
          required
          multiline
          rows={4}
        />

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end" }}>
          
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <InputLabel sx={{ mb: 0 }}>Contenido</InputLabel>
            <input
              type="color"
              value={colorCardBase}
              onChange={(e) => setColorCardBase(e.target.value)}
              style={{
                width: "60px",
                height: "40px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <InputLabel sx={{ mb: 0 }}>Texto</InputLabel>
            <input
              type="color"
              value={colorTexto}
              onChange={(e) => setColorTexto(e.target.value)}
              style={{
                width: "60px",
                height: "40px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            />
          </Box>
          <FormControl sx={{ minWidth: 150, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={estado}
              onChange={(e) =>
                setEstado(e.target.value as "activo" | "inactivo")
              }
              label="Estado"
              size="small"
            >
              <MenuItem value="activo">Activo</MenuItem>
              <MenuItem value="inactivo">Inactivo</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box>
          <InputLabel sx={{ mb: 1 }}>Vista Previa</InputLabel>
          <Box
            sx={{
              border: 2,
              borderColor: "divider",
              borderRadius: 2,
              p: 3,
              background: `linear-gradient(180deg, ${gradient.color1}, ${gradient.color2})`,
              minHeight: 150,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              width: { xs: "100%", md: "50%", lg: "25%" },
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: colorTexto,
                mb: 1.5,
                fontSize: "1.5rem",
              }}
            >
              {titulo || "Título de ejemplo"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colorTexto,
                fontSize: "0.95rem",
                fontWeight: "normal",
              }}
            >
              {descripcion || "Descripción de ejemplo para la promoción"}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider sx={{ marginTop: 2 }} />
      <Box display="flex" gap={2} p={2}>
        <Button onClick={onClose} sx={{ mr: 1 }}>
          Cancelar
        </Button>
        <Box sx={{ flex: "1 1 auto" }} />
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || !titulo.trim() || !descripcion.trim()}
        >
          {isLoading ? "Guardando..." : "Guardar"}
        </Button>
      </Box>
    </>
  );
}
